import { useEffect, useRef } from 'react';

/**
 * Reserved ad space that loads Google AdSense only when a publisher id is set:
 *   VITE_ADSENSE_CLIENT_ID=ca-pub-XXXXXXXXXXXXXXXX
 * While unset it renders an invisible reserved box (no empty "Advertisement"
 * boxes on the live site, but zero layout shift when ads turn on later).
 * Never rendered inside the admin dashboard.
 */
const CLIENT_ID: string | undefined = import.meta.env.VITE_ADSENSE_CLIENT_ID;

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let scriptLoading = false;
function ensureAdScript() {
  if (scriptLoading || !CLIENT_ID) return;
  if (document.querySelector('script#adsbygoogle')) return;
  scriptLoading = true;
  const s = document.createElement('script');
  s.id = 'adsbygoogle';
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT_ID}`;
  document.head.appendChild(s);
}

type Variant = 'leaderboard' | 'inline' | 'sidebar';

const HEIGHTS: Record<Variant, number> = {
  leaderboard: 96,
  inline: 120,
  sidebar: 280
};

const SLOT_IDS: Record<Variant, string> = {
  leaderboard: '1000000001',
  inline: '1000000002',
  sidebar: '1000000003'
};

export function AdSlot({ variant, label }: { variant: Variant; label?: string }) {
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!CLIENT_ID) return;
    ensureAdScript();
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* console blocked */
    }
  }, [variant]);

  return (
    <div
      ref={box}
      className={`ad-slot ${variant}`}
      style={{ minHeight: CLIENT_ID ? HEIGHTS[variant] : 0 }}
      role="complementary"
      aria-label={label ?? 'Advertisement space'}
      data-ad-reserved={CLIENT_ID ? undefined : 'true'}
    >
      {CLIENT_ID ? (
        <ins
          className="adsbygoogle"
          style={{ display: 'block', width: '100%' }}
          data-ad-client={CLIENT_ID}
          data-ad-slot={SLOT_IDS[variant]}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      ) : null}
    </div>
  );
}
